import { createContext, useContext, useState, useMemo, createElement } from 'react';
import cloneDeep from 'lodash.clonedeep';
import ky, { HTTPError } from 'ky';
import { config, version, delayAsync, parseAndReviveJson, AppError, AppErrorType, AppErrorTypeGroupNames, EventFormat, correctPageIndex, generateSortingOptions } from '@bujus/common';
import Fuse from 'fuse.js';

const AuthorizationJwtStateContext = createContext();
const AuthorizationJwtStateProvider = AuthorizationJwtStateContext.Provider;
const useAuthorizationJwtStateContext = () => useContext(AuthorizationJwtStateContext);

const ApiServiceContext = createContext();
const ApiServiceProvider = ApiServiceContext.Provider;
const useApiServiceContext = () => useContext(ApiServiceContext);

const FormControllerContext = createContext();
const FormControllerProvider = FormControllerContext.Provider;
const useFormControllerContext = () => useContext(FormControllerContext);

const LogoutLocalContext = createContext();
const LogoutLocalProvider = LogoutLocalContext.Provider;
const useLogoutLocalContext = () => useContext(LogoutLocalContext);

const useDialogDisplayer = (Dialog) => {
  const [isOpening, setIsOpening] = useState(false);
  const [props, setProps] = useState(void 0);
  const open = (newProps = {}) => {
    if (isOpening || props !== void 0) {
      return;
    }
    setIsOpening(true);
    setProps(cloneDeep(newProps));
  };
  const close = () => {
    setIsOpening(false);
    setTimeout(() => {
      setProps(void 0);
    }, 200);
  };
  const element = useMemo(() => {
    if (props === void 0) {
      return void 0;
    }
    return createElement(Dialog, {
      ...props,
      close,
      isOpen: isOpening
    });
  }, [Dialog, isOpening, props]);
  const dialogDisplayer = {
    isOpen: isOpening,
    open,
    close,
    element
  };
  return dialogDisplayer;
};

const useFormValidationRule = (formController, field, ruleName) => {
    let schema = formController?.validationSchema;
    field.split(".").forEach((x) => {
        schema = schema?._ids?._byKey?.get(x)?.schema;
    });
    return schema?._rules?.find((rule) => rule.name === ruleName);
};

const generateApiHttpService = (
    getAuthorizationJwt,
    onAuthorizationRefreshSuccess,
    onAuthorizationRefreshError,
    authorizationRefreshUrl,
    refreshAuthorizationAsync,
    onVersionReceived = undefined,
) => {
    const kyInstance = ky.create({
        credentials: "include",
        timeout: config.timeout,
        hooks: {
            beforeRequest: [
                (request) => {
                    request.headers.set("app-version", version);
                },
                (request) => {
                    const authorizationJwt = getAuthorizationJwt();
                    if (authorizationJwt === undefined) {
                        return;
                    }
                    request.headers.set("authorization", `Bearer ${authorizationJwt}`);
                },
            ],
            afterResponse: [
                (_request, _options, response) => {
                    if (onVersionReceived !== undefined) {
                        onVersionReceived(response.headers.get("api-version"));
                    }
                    return response;
                },
                async (request, options, response) => {
                    if (options.isSecondTry) {
                        // Retrying is build into ky, this is only for the second request after authorization refresh
                        return response;
                    }
                    if (
                        new URL(response.url).pathname ===
                            new URL(authorizationRefreshUrl).pathname &&
                        response.status === 201
                    ) {
                        onAuthorizationRefreshSuccess(
                            options.parseJson(await response.clone().text()),
                        );
                        await delayAsync(500); // Time to set cookie or store value
                        return response;
                    }
                    if (
                        response.url.startsWith(authorizationRefreshUrl) ||
                        response.status !== 401
                    ) {
                        return response;
                    }
                    try {
                        await refreshAuthorizationAsync();
                    } catch (error) {
                        onAuthorizationRefreshError(error);
                        return error.originalError;
                    }
                    return kyInstance(request, { isSecondTry: true });
                },
            ],
        },
        parseJson: parseAndReviveJson,
    });

    const doAsyncRequestAsync = async (requestAsync) => {
        let response;
        try {
            response = await requestAsync();
        } catch (error) {
            if (!(error instanceof HTTPError)) {
                // Maybe ky TimeoutError
                throw new AppError(AppErrorType.NETWORK, undefined, error);
            }
            const data = await error.response.json();
            throw new AppError(data.type, data.details, error, data.customMessage);
        }
        return response.json();
    };

    const postAsync = async (url, body) =>
        doAsyncRequestAsync(async () => kyInstance.post(url, { json: body }));

    const getAsync = async (url, body) =>
        doAsyncRequestAsync(async () => kyInstance.get(url, { json: body }));

    const putAsync = async (url, body) =>
        doAsyncRequestAsync(async () => kyInstance.put(url, { json: body }));

    const removeAsync = async (url, body) =>
        doAsyncRequestAsync(async () => kyInstance.delete(url, { json: body }));

    return {
        postAsync,
        getAsync,
        putAsync,
        removeAsync,
    };
};

const generateCertTemplateSetsApiService = (apiRoutes, apiHttpService) => {
    const getAsync = async (eventId) =>
        apiHttpService.getAsync(apiRoutes().certTemplateSets().get().build({ eventId }));

    const printTestAsync = async (eventId, certType) =>
        apiHttpService.postAsync(
            apiRoutes().certTemplateSets().printTest().build({
                eventId,
                certType,
            }),
        );

    const updateAsync = async (eventId, certType, presetId, certTemplate) =>
        apiHttpService.putAsync(
            apiRoutes().certTemplateSets().update().build({
                eventId,
                certType,
            }),
            {
                presetId,
                certTemplate,
            },
        );

    const updateRubberStampAsync = async (eventId, rubberStampPngDataUrl) =>
        apiHttpService.putAsync(
            apiRoutes().certTemplateSets().updateRubberStamp().build({ eventId }),
            { rubberStampPngDataUrl },
        );

    const updateSignatureAsync = async (eventId, signaturePngDataUrl) =>
        apiHttpService.putAsync(
            apiRoutes().certTemplateSets().updateSignature().build({ eventId }),
            { signaturePngDataUrl },
        );

    return {
        getAsync,
        printTestAsync,
        updateAsync,
        updateRubberStampAsync,
        updateSignatureAsync,
    };
};

const checkIsDialogOpen = (dialogDisplayers) => dialogDisplayers.some((x) => x.isOpen);

const checkIsErrorLogoutReason = (error) =>
    error.type !== undefined && error.type.startsWith(AppErrorTypeGroupNames.UNAUTHORIZED);

const convertListingModifiersToQueryParams = (listingModifiers) => {
    if (listingModifiers.searchValue !== undefined) {
        return {
            searchValue: listingModifiers.searchValue,
            pageSize: listingModifiers.pageSize,
        };
    }
    return {
        fieldSortingPath: listingModifiers.fieldSorting.path, // TODO Migrate to fieldSorting_path
        fieldSortingOrder: listingModifiers.fieldSorting.order,
        pageSize: listingModifiers.pageSize,
        pageIndex: listingModifiers.pageIndex,
    };
};

const EventFormatComponent = ({
  contestEventComponent,
  competitionEventComponent,
  eventFormat,
  ...props
}) => {
  switch (eventFormat) {
    case EventFormat.CONTEST:
      return createElement(contestEventComponent, props);
    case EventFormat.COMPETITION:
      return createElement(competitionEventComponent, props);
    default:
      throw new Error(`Invalid event format: ${eventFormat}`);
  }
};

// https://stripe.com/docs/payments/intents?intent=payment#intent-statuses
// https://stripe.com/docs/invoicing/overview#workflow-overview
const formatInvoiceStatus = (invoice) => {
    const strings = [];
    switch (invoice.status) {
        // Draft invoices are not sent to the client
        // case "draft":
        //     return "Entwurf - ${string}";
        case "open":
            strings.push("Offen");
            break;
        case "paid":
            strings.push("Bezahlt");
            break;
        case "void":
            strings.push("Storniert");
            break;
        case "uncollectible":
            strings.push("Nicht bezahlt");
            break;
        default:
            strings.push("Unbekannt");
            break;
    }
    if (invoice.paymentIntent !== undefined) {
        switch (invoice.paymentIntent.status) {
            case "requires_payment_method":
                strings.push("Bezahlmethode erforderlich");
                break;
            case "requires_confirmation":
                strings.push("Zahlung erforderlich");
                break;
            case "requires_action":
                strings.push("Aktion erforderlich");
                break;
            case "processing":
                strings.push("In Bearbeitung");
                break;
            case "requires_capture":
                strings.push("Erfassung erforderlich");
                break;
        }
    }
    return strings.join(" - ");
};

const generateSelectOptions = (items, getKeyFromItem, formatItem) =>
    items.map((x, xIndex) => ({
        key: getKeyFromItem(x, xIndex),
        value: x,
        text: formatItem === undefined ? x : formatItem(x, xIndex),
    }));

/**
 * @typedef ListingModifiers
 * @property searchValue
 * @property sorting
 * @property pageSize
 * @property pageIndex
 *
 * @param {ListingModifiers} listingModifiers
 */
const listItemsWithFuse = (items, fields, defaultSorting, listingModifiers) => {
    if (listingModifiers.searchValue !== undefined) {
        return {
            searchValue: listingModifiers.searchValue,
            pageSize: listingModifiers.pageSize,
            items: new Fuse(items, { keys: fields })
                .search(listingModifiers.searchValue.replace(/\s+/g, " ").trim())
                .map((x) => x.item),
        };
    }
    const pageIndex = correctPageIndex(listingModifiers, items.length);
    return {
        pageSize: listingModifiers.pageSize,
        pageIndex,
        count: items.length,
        items: items
            .sort(generateSortingOptions(defaultSorting, listingModifiers))
            .slice(
                pageIndex * listingModifiers.pageSize,
                (pageIndex + 1) * listingModifiers.pageSize,
            ),
    };
};

const ResultCreationHistoryState = {
    LOADING: "LOADING",
    ERROR: "ERROR",
    SUCCESS: "SUCCESS",
};

const generateCertsApiService = (apiRoutes, apiHttpService) => {
    const generateAllAsync = (eventId, options) =>
        apiHttpService.postAsync(apiRoutes().certs().generateAll().build({ eventId }), { options });

    const printAsync = (id) =>
        apiHttpService.postAsync(apiRoutes().certs().print(false, { id }).build());

    const listOnlyNotGeneratedAsync = (eventId, listingModifiers) =>
        apiHttpService.getAsync(
            apiRoutes()
                .certs()
                .list()
                .build({
                    eventId,
                    isOnlyNotGenerated: true,
                    ...convertListingModifiersToQueryParams(listingModifiers),
                }),
        );

    const listOnlyGeneratedAsync = (eventId, listingModifiers) =>
        apiHttpService.getAsync(
            apiRoutes()
                .certs()
                .list()
                .build({
                    eventId,
                    isOnlyGenerated: true,
                    ...convertListingModifiersToQueryParams(listingModifiers),
                }),
        );

    const printOverviewOfAllAsync = (eventId) =>
        apiHttpService.postAsync(apiRoutes().certs().printOverviewOfAll().build({ eventId }));

    const exportAllAsync = (eventId) =>
        apiHttpService.postAsync(apiRoutes().certs().exportAll().build({ eventId }));

    const printAllAsync = (eventId, sorting) =>
        apiHttpService.postAsync(
            apiRoutes().certs().printAll().build({
                eventId,
                sortingPreset: sorting.preset,
                sortingOrder: sorting.order,
            }),
        );

    return {
        generateAllAsync,
        printAsync,
        listOnlyNotGeneratedAsync,
        listOnlyGeneratedAsync,
        printOverviewOfAllAsync,
        exportAllAsync,
        printAllAsync,
    };
};

const generateDisciplinePreselectionsApiService = (apiRoutes, apiHttpService) => {
    const getAsync = async (eventId) =>
        apiHttpService.getAsync(apiRoutes().disciplinePreselection().get().build({ eventId }));

    const updateAsync = async (eventId, disciplinePreselection) =>
        apiHttpService.putAsync(apiRoutes().disciplinePreselection().update().build({ eventId }), {
            disciplinePreselection,
        });

    return {
        getAsync,
        updateAsync,
    };
};

const generateEventsApiService = (apiRoutes, apiHttpService) => {
    const getPricingAsync = () =>
        apiHttpService.getAsync(apiRoutes().events().getPricing().build());

    const createAsync = async (
        schoolId,
        event,
        discountCode = undefined,
        paymentType = undefined,
    ) =>
        apiHttpService.postAsync(apiRoutes().events().create().build({ schoolId }), {
            event,
            ...(discountCode !== undefined && { discountCode }),
            ...(paymentType !== undefined && { paymentType }),
        });

    const generateReportAsync = async (id) =>
        apiHttpService.postAsync(apiRoutes().events().generateReport(false, { id }).build());

    const getAsync = async (id) =>
        apiHttpService.getAsync(apiRoutes().events().get(false, { id }).build());

    const getCheckoutCompletedResultAsync = async (stripeSessionId) =>
        apiHttpService.getAsync(
            apiRoutes().events().getCheckoutCompletedResult(false, { stripeSessionId }).build(),
        );

    const listAsync = async (schoolId, listingModifiers) =>
        apiHttpService.getAsync(
            apiRoutes()
                .events()
                .list()
                .build({
                    schoolId,
                    ...convertListingModifiersToQueryParams(listingModifiers),
                }),
        );

    const updateNameAsync = async (id, name) =>
        apiHttpService.putAsync(apiRoutes().events().updateName(false, { id }).build(), { name });

    const requestRemovalAsync = async (id, password) =>
        apiHttpService.postAsync(apiRoutes().events().requestRemoval(false, { id }).build(), {
            password,
        });

    const validateRemovalTokenAsync = async (token) =>
        apiHttpService.getAsync(
            apiRoutes().events().validateRemovalToken(false, { token }).build(),
        );

    const cancelRemovalAsync = async (id) =>
        apiHttpService.postAsync(apiRoutes().events().cancelRemoval(false, { id }).build());

    const removeAsync = async (token, password) =>
        apiHttpService.removeAsync(apiRoutes().events().remove(false, { token }).build(), {
            password,
        });

    return {
        getPricingAsync,
        createAsync,
        generateReportAsync,
        getAsync,
        getCheckoutCompletedResultAsync,
        listAsync,
        updateNameAsync,
        requestRemovalAsync,
        validateRemovalTokenAsync,
        cancelRemovalAsync,
        removeAsync,
    };
};

// import { convertListingModifiersToQueryParams } from "@/utilities";

const generateGroupsApiService = (apiRoutes, apiHttpService) => {
    const createAsync = async (group) =>
        apiHttpService.postAsync(apiRoutes().groups().create().build(), { group });

    const getAllAsync = async (eventId) =>
        apiHttpService.getAsync(apiRoutes().groups().getAll(false).build({ eventId }));

    const updateAsync = async (id, group) =>
        apiHttpService.putAsync(apiRoutes().groups().update(false, { id }).build(), { group });

    const updateAllAsync = async (eventId, groups) =>
        apiHttpService.putAsync(apiRoutes().groups().updateAll(false).build({ eventId }), {
            groups,
        });

    const transferParticipantsAsync = async (sourceId, targetId, participantIds) =>
        apiHttpService.putAsync(
            apiRoutes().groups().transferParticipants(false, { sourceId }).build(),
            {
                targetId,
                participantIds,
            },
        );

    const removeAsync = async (id) =>
        apiHttpService.removeAsync(apiRoutes().groups().remove(false, { id }).build());

    return {
        createAsync,
        getAllAsync,
        updateAsync,
        updateAllAsync,
        transferParticipantsAsync,
        removeAsync,
    };
};

const generateHelpersApiService = (apiRoutes, apiHttpService) => {
    const createAsync = async (isGeneratingUsername, isGeneratingPassword, helper) =>
        apiHttpService.postAsync(apiRoutes().helpers().create().build(), {
            isGeneratingUsername,
            isGeneratingPassword,
            helper,
        });

    const importAsync = async (
        eventId,
        areNamesSpecified,
        isGeneratingUsernames,
        isGeneratingPasswords,
        helpers,
    ) =>
        apiHttpService.postAsync(apiRoutes().helpers().import().build(), {
            eventId,
            areNamesSpecified,
            isGeneratingUsernames,
            isGeneratingPasswords,
            helpers,
        });

    const loginAsync = async (event3WordId, username, password) =>
        apiHttpService.postAsync(apiRoutes().helpers().login().build({ event3WordId }), {
            username,
            password,
        });

    const reloginAsync = async (helperId, password) =>
        apiHttpService.postAsync(apiRoutes().helpers().relogin().build({ helperId }), { password });

    const refreshAuthorizationAsync = async (token) =>
        apiHttpService.postAsync(apiRoutes().helpers().refreshAuthorization().build({ token }));

    const logoutAsync = async (token) =>
        apiHttpService.postAsync(apiRoutes().helpers().logout().build({ token }));

    const getAsync = async (id) =>
        apiHttpService.getAsync(apiRoutes().helpers().get(false, { id }).build());

    const listAsync = async (eventId, listingModifiers) =>
        apiHttpService.getAsync(
            apiRoutes()
                .helpers()
                .list()
                .build({
                    eventId,
                    ...convertListingModifiersToQueryParams(listingModifiers),
                }),
        );

    const printLoginDataOfAllAsync = async (eventId) =>
        apiHttpService.postAsync(apiRoutes().helpers().printLoginDataOfAll().build({ eventId }));

    const updateAsync = async (id, helper) =>
        apiHttpService.putAsync(apiRoutes().helpers().update(false, { id }).build(), { helper });

    const updateIsActiveOfAllAsync = async (eventId, isActive) =>
        apiHttpService.putAsync(apiRoutes().helpers().updateIsActiveOfAll().build({ eventId }), {
            isActive,
        });

    const removeAsync = async (id, isResultsKeepingEnabled) =>
        apiHttpService.removeAsync(apiRoutes().helpers().remove(false, { id }).build(), {
            isResultsKeepingEnabled,
        });

    const removeManyAsync = async (ids) =>
        apiHttpService.removeAsync(apiRoutes().helpers().removeMany().build(), { ids });

    return {
        createAsync,
        importAsync,
        loginAsync,
        reloginAsync,
        refreshAuthorizationAsync,
        logoutAsync,
        getAsync,
        listAsync,
        printLoginDataOfAllAsync,
        updateAsync,
        updateIsActiveOfAllAsync,
        removeAsync,
        removeManyAsync,
    };
};

const generateParticipantsApiService = (apiRoutes, apiHttpService) => {
    const createAsync = async (participant) =>
        apiHttpService.postAsync(apiRoutes().participants().create().build(), { participant });

    const importAsync = async (
        eventId,
        areSchoolInternalIdsSpecified,
        areClassesSpecified,
        areGroupsSpecified,
        participants,
    ) =>
        apiHttpService.postAsync(apiRoutes().participants().import().build(), {
            eventId,
            areSchoolInternalIdsSpecified,
            areClassesSpecified,
            areGroupsSpecified,
            participants,
        });

    const listAsync = async (eventId, listingModifiers) =>
        apiHttpService.getAsync(
            apiRoutes()
                .participants()
                .list()
                .build({
                    eventId,
                    ...convertListingModifiersToQueryParams(listingModifiers),
                }),
        );

    const printStarterlistOfAllAsync = async (eventId, disciplineLevelOfDetail, isNotesFriendly) =>
        apiHttpService.postAsync(
            apiRoutes().participants().printStarterlistOfAll().build({
                eventId,
                disciplineLevelOfDetail,
                isNotesFriendly,
            }),
        );

    const updateAsync = async (id, participant) =>
        apiHttpService.putAsync(apiRoutes().participants().update(false, { id }).build(), {
            participant,
        });

    const removeAsync = async (id) =>
        apiHttpService.removeAsync(apiRoutes().participants().remove(false, { id }).build());

    const removeManyAsync = async (ids) =>
        apiHttpService.removeAsync(apiRoutes().participants().removeMany().build(), { ids });

    return {
        createAsync,
        importAsync,
        listAsync,
        printStarterlistOfAllAsync,
        updateAsync,
        removeAsync,
        removeManyAsync,
    };
};

const generateResultsApiService = (apiRoutes, apiHttpService) => {
    const createAsync = async (result) =>
        apiHttpService.postAsync(apiRoutes().results().create().build(), { result });

    const importInHelperAsync = async (helperId, results) =>
        apiHttpService.postAsync(apiRoutes().results().import().build(), {
            helperId,
            results,
        });

    const listOfEventAsync = async (eventId, listingModifiers) =>
        apiHttpService.getAsync(
            apiRoutes()
                .results()
                .list()
                .build({
                    eventId,
                    ...convertListingModifiersToQueryParams(listingModifiers),
                }),
        );

    const listOfHelperAsync = async (helperId, listingModifiers) =>
        apiHttpService.getAsync(
            apiRoutes()
                .results()
                .list()
                .build({
                    helperId,
                    ...convertListingModifiersToQueryParams(listingModifiers),
                }),
        );

    const getAllOfParticipantAndDisciplineAsync = async (participantId, disciplineId) =>
        apiHttpService.getAsync(
            apiRoutes().results().getAllOfParticipantAndDiscipline().build({
                participantId,
                disciplineId,
            }),
        );

    const exportAllAsync = async (eventId) =>
        apiHttpService.postAsync(apiRoutes().results().exportAll().build({ eventId }));

    const updateAsync = async (id, result) =>
        apiHttpService.putAsync(apiRoutes().results().update(false, { id }).build(), { result });

    const removeAsync = async (id) =>
        apiHttpService.removeAsync(apiRoutes().results().remove(false, { id }).build());

    const removeManyAsync = async (ids) =>
        apiHttpService.removeAsync(apiRoutes().results().removeMany().build(), { ids });

    return {
        createAsync,
        importInHelperAsync,
        listOfEventAsync,
        listOfHelperAsync,
        getAllOfParticipantAndDisciplineAsync,
        exportAllAsync,
        updateAsync,
        removeAsync,
        removeManyAsync,
    };
};

const generateSchoolsApiService = (apiRoutes, apiHttpService) => {
    const signUpAsync = async (school) =>
        apiHttpService.postAsync(apiRoutes().schools().signUp().build(), { school });

    const loginAsync = async (mailAddress, password) =>
        apiHttpService.postAsync(apiRoutes().schools().login().build(), {
            mailAddress,
            password,
        });

    const refreshAuthorizationAsync = async () =>
        apiHttpService.postAsync(apiRoutes().schools().refreshAuthorization().build());

    const logoutAsync = async () =>
        apiHttpService.postAsync(apiRoutes().schools().logout().build());

    const getAsync = async (id) =>
        apiHttpService.getAsync(apiRoutes().schools().get(false, { id }).build());

    const getAllPaymentsAsync = async (id) =>
        apiHttpService.getAsync(apiRoutes().schools().getAllPayments(false, { id }).build());

    const updateAsync = async (id, school) =>
        apiHttpService.putAsync(apiRoutes().schools().update(false, { id }).build(), { school });

    const requestDpaAsync = async (id, signer) =>
        apiHttpService.postAsync(apiRoutes().schools().requestDpa(false, { id }).build(), {
            signer,
        });

    const printDpaAsync = async (id) =>
        apiHttpService.postAsync(apiRoutes().schools().printDpa(false, { id }).build());

    const updateDataPrivacyAsync = async (id, dataPrivacy) =>
        apiHttpService.putAsync(apiRoutes().schools().updateDataPrivacy(false, { id }).build(), {
            dataPrivacy,
        });

    const updateFlowAsync = async (id, flow) =>
        apiHttpService.putAsync(apiRoutes().schools().updateFlow(false, { id }).build(), { flow });

    const requestMailAddressChangeAsync = async (id, verifiableMailAddress) =>
        apiHttpService.postAsync(
            apiRoutes().schools().requestMailAddressChange(false, { id }).build(),
            { verifiableMailAddress },
        );

    const validateMailAddressVerificationTokenAsync = async (token) =>
        apiHttpService.getAsync(
            apiRoutes().schools().validateMailAddressVerificationToken(false, { token }).build(),
        );

    const cancelMailAddressChangeAsync = async (id) =>
        apiHttpService.postAsync(
            apiRoutes().schools().cancelMailAddressChange(false, { id }).build(),
        );

    const verifyMailAddressAsync = async (token) =>
        apiHttpService.postAsync(apiRoutes().schools().verifyMailAddress(false, { token }).build());

    const forgotPasswordAsync = async (mailAddress) =>
        apiHttpService.postAsync(apiRoutes().schools().forgotPassword().build(), { mailAddress });

    const requestPasswordChangeAsync = async (id) =>
        apiHttpService.postAsync(
            apiRoutes().schools().requestPasswordChange(false, { id }).build(),
        );

    const validatePasswordChangeTokenAsync = async (token) =>
        apiHttpService.getAsync(
            apiRoutes().schools().validatePasswordChangeToken(false, { token }).build(),
        );

    const cancelPasswordChangeAsync = async (id) =>
        apiHttpService.postAsync(apiRoutes().schools().cancelPasswordChange(false, { id }).build());

    const changePasswordAsync = async (token, password) =>
        apiHttpService.postAsync(apiRoutes().schools().changePassword(false, { token }).build(), {
            password,
        });

    const requestRemovalAsync = async (id, password) =>
        apiHttpService.postAsync(apiRoutes().schools().requestRemoval(false, { id }).build(), {
            password,
        });

    const validateRemovalTokenAsync = async (token) =>
        apiHttpService.getAsync(
            apiRoutes().schools().validateRemovalToken(false, { token }).build(),
        );

    const cancelRemovalAsync = async (id) =>
        apiHttpService.postAsync(apiRoutes().schools().cancelRemoval(false, { id }).build());

    const removeAsync = async (token, password) =>
        apiHttpService.removeAsync(apiRoutes().schools().remove(false, { token }).build(), {
            password,
        });

    return {
        signUpAsync,
        loginAsync,
        refreshAuthorizationAsync,
        logoutAsync,
        getAsync,
        getAllPaymentsAsync,
        updateAsync,
        requestDpaAsync,
        printDpaAsync,
        updateDataPrivacyAsync,
        updateFlowAsync,
        requestMailAddressChangeAsync,
        validateMailAddressVerificationTokenAsync,
        cancelMailAddressChangeAsync,
        verifyMailAddressAsync,
        forgotPasswordAsync,
        requestPasswordChangeAsync,
        validatePasswordChangeTokenAsync,
        cancelPasswordChangeAsync,
        changePasswordAsync,
        requestRemovalAsync,
        validateRemovalTokenAsync,
        cancelRemovalAsync,
        removeAsync,
    };
};

const useApiService = (
    apiRoutes,
    getAuthorizationJwt,
    authorizationJwtMirror,
    onAuthorizationRefreshSuccess,
    onAuthorizationRefreshError,
    authorizationRefreshUrl,
    refreshAuthorizationAsync,
    onVersionReceived = undefined,
) =>
    useMemo(() => {
        const apiHttpService = generateApiHttpService(
            getAuthorizationJwt,
            onAuthorizationRefreshSuccess,
            onAuthorizationRefreshError,
            authorizationRefreshUrl,
            refreshAuthorizationAsync,
            onVersionReceived,
        );
        return {
            schools: generateSchoolsApiService(apiRoutes, apiHttpService),
            events: generateEventsApiService(apiRoutes, apiHttpService),
            disciplinePreselections: generateDisciplinePreselectionsApiService(
                apiRoutes,
                apiHttpService,
            ),
            helpers: generateHelpersApiService(apiRoutes, apiHttpService),
            participants: generateParticipantsApiService(apiRoutes, apiHttpService),
            groups: generateGroupsApiService(apiRoutes, apiHttpService),
            results: generateResultsApiService(apiRoutes, apiHttpService),
            certTemplateSets: generateCertTemplateSetsApiService(apiRoutes, apiHttpService),
            certs: generateCertsApiService(apiRoutes, apiHttpService),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        getAuthorizationJwt,
        onAuthorizationRefreshSuccess,
        onAuthorizationRefreshError,
        authorizationRefreshUrl,
        refreshAuthorizationAsync,
        apiRoutes,
        authorizationJwtMirror,
    ]);

export { ApiServiceContext, ApiServiceProvider, AuthorizationJwtStateContext, AuthorizationJwtStateProvider, EventFormatComponent, FormControllerContext, FormControllerProvider, LogoutLocalContext, LogoutLocalProvider, ResultCreationHistoryState, checkIsDialogOpen, checkIsErrorLogoutReason, convertListingModifiersToQueryParams, formatInvoiceStatus, generateSelectOptions, listItemsWithFuse, useApiService, useApiServiceContext, useAuthorizationJwtStateContext, useDialogDisplayer, useFormControllerContext, useFormValidationRule, useLogoutLocalContext };
//# sourceMappingURL=common-frontend.js.map
